/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Form, FormGroup } from 'react-bootstrap'
import { FieldArrayRenderProps, useFormikContext } from 'formik'
import _ from 'lodash'
import { useServiceContext } from '../../root'
import { Select } from '../../components/Select'
import { TYPE } from '../../screens/settings/properties/Actions/EditFormConstants'
import CustomErrorMessage from '../../components/shared/ErrorMessage'
import { PROPERTY_SEARCH_ROW_CONDITION_VALUE, metadataCriteriaEmptyRow } from './constants'
import SelectUserGroup from '~/components/SelectUserGroup'

interface PropertyOption {
  id: number | string
  name: string
  label: string
  value: string | number
  type?: string
  definition?: {
    options: any[]
  }
}

interface PropertyDefinitionResult {
  definition?: {
    options: any[]
  }
}

interface MetadataCriteriaItem {
  value: string | number | null | any[]
  metadataTemplateId: number | string | null
  condition: string
  includeInherited: boolean
  property?: PropertyOption
}

interface PropertyFieldsProps {
  arrayHelpers: FieldArrayRenderProps
  values: MetadataCriteriaItem[]
}

const transformToSelectOptions = (
  elements: any[],
  valueKey: string,
  labelKey: string,
  nameKey: string,
  includeId: boolean
) => {
  return elements.map((element) => ({
    value: element[valueKey],
    label: element[labelKey],
    name: element[nameKey],
    ...(includeId && { id: element[valueKey] }),
    ...element
  }))
}

function PropertyFields({ arrayHelpers, values }: PropertyFieldsProps) {
  const context = useServiceContext()
  const translate = context.service.filter.translate
  const { setFieldValue } = useFormikContext()
  const [properties, setProperties] = useState<PropertyOption[]>([])

  useEffect(() => {
    context.api.metadata.getAll({ sortBy: 'NAME', sortDesc: false }).then((data: any) => {
      setProperties(transformToSelectOptions(data.elements, 'id', 'name', 'name', true))
    })

    if (Array.isArray(values) && values.length === 0) {
      addStep()
    }
  }, [])

  const convertToArrayOfObjects = (options: any[]) => {
    return options.map((i) => {
      return { label: i, value: i }
    })
  }

  const addStep = () => {
    arrayHelpers.push({ ...metadataCriteriaEmptyRow })
  }

  const removeStep = (index: number) => {
    arrayHelpers.remove(index)
  }

  const updateStep = (newData: MetadataCriteriaItem, index: number) => {
    setFieldValue(`${arrayHelpers.name}.${index}`, { ...newData })
  }

  const getConditionOptions = (type: string) => {
    switch (type) {
      case TYPE.DATETIME:
        return [
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE, label: translate('OPTION_IS_USED') },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.EQUALS, label: translate('OPTION_ON') },
          {
            value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.LESS_THAN_OR_EQUALS_TO,
            label: translate('OPTION_BEFORE_OR_ON')
          },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.LESS_THAN, label: translate('OPTION_BEFORE') },
          {
            value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.GREATER_THAN_OR_EQUALS_TO,
            label: translate('OPTION_AFTER_OR_ON')
          },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.GREATER_THAN, label: translate('OPTION_AFTER') },
          {
            value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.PREVIOUS_N_DAYS,
            label: translate('OPTION_PREVIOUS_N_DAYS')
          },
          {
            value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.NEXT_N_DAYS,
            label: translate('OPTION_NEXT_N_DAYS')
          }
        ]
      case TYPE.NUMBER:
        return [
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE, label: translate('OPTION_IS_USED') },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.EQUALS, label: '=' },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.LESS_THAN_OR_EQUALS_TO, label: '<=' },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.LESS_THAN, label: '<' },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.GREATER_THAN_OR_EQUALS_TO, label: '>=' },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.GREATER_THAN, label: '>' }
        ]
      case TYPE.ARTIFACT:
      case TYPE.STRING:
        return [
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE, label: translate('OPTION_IS_USED') },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.EQUALS, label: translate('OPTION_EQUALS') },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.CONTAINS, label: translate('OPTION_CONTAINS') }
        ]
      case TYPE.SELECT_ONE:
      case TYPE.SELECT_ONE_USER:
      case TYPE.SELECT_ONE_GROUP:
        return [
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE, label: translate('OPTION_IS_USED') },
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.EQUALS_ANY_OF, label: translate('OPTION_EQUALS') }
        ]
      case TYPE.SELECT_MULTI:
      case TYPE.SELECT_MULTI_USER:
      case TYPE.SELECT_MULTI_GROUP:
        return [
          { value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE, label: translate('OPTION_IS_USED') },
          {
            value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.CONTAINS_ANY,
            label: translate('OPTION_ALL_THAT_HAVE_ATLEAST_ONE_OF_THESE')
          },
          {
            value: PROPERTY_SEARCH_ROW_CONDITION_VALUE.CONTAINS_ALL,
            label: translate('OPTION_MUST_HAVE_ALL')
          }
        ]
      default:
        return []
    }
  }

  return (
    <FormGroup>
      {values.map((item, index) => (
        <div className="clearfix property-search-filter-row" key={index}>
          {index > 0 && (
            <button
              className="close-x"
              onClick={() => {
                removeStep(index)
              }}
            />
          )}

          <div className="inpt-wrpr mb5">
            <Select
              name={`${arrayHelpers.name}.${index}.metadataTemplateId`}
              value={item.metadataTemplateId}
              options={properties}
              placeholder={translate('SELECT_PROPERTY...')}
              isClearable={false}
              onChange={(option: any) => {
                ;(async () => {
                  item.metadataTemplateId = option.id
                  // Preserve all properties including type
                  item.property = {
                    ...option,
                    type: option.type,
                    definition: option.definition
                  }
                  item.value = ''
                  item.condition = PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE
                  if (option.type && [TYPE.SELECT_ONE, TYPE.SELECT_MULTI].indexOf(option.type) > -1) {
                    item.property!.definition!.options = convertToArrayOfObjects(item.property!.definition!.options)
                  }
                  if (option?.type === TYPE.ARTIFACT && option?.id) {
                    const propertyDefinitionResult = (await context.api.metadata.getOneProperty(
                      option.id
                    )) as PropertyDefinitionResult
                    if (propertyDefinitionResult?.definition?.options) {
                      item.property!.definition!.options = convertToArrayOfObjects(
                        propertyDefinitionResult.definition.options
                      )
                    }
                  }
                  updateStep(item, index)
                })()
              }}
              key={index}
              searchable
            />
            <CustomErrorMessage name={`${arrayHelpers.name}.${index}.metadataTemplateId`} />
          </div>

          {item.property && item.property.type && (
            <div className="inpt-wrpr mb5">
              <Select
                name={`${arrayHelpers.name}.${index}.condition`}
                value={item.condition}
                options={getConditionOptions(item.property?.type)}
                isClearable={false}
                onChange={(option: any) => {
                  const newValue =
                    _.includes([TYPE.STRING, TYPE.NUMBER], item.property?.type) ||
                    _.includes(
                      [
                        PROPERTY_SEARCH_ROW_CONDITION_VALUE.NEXT_N_DAYS,
                        PROPERTY_SEARCH_ROW_CONDITION_VALUE.PREVIOUS_N_DAYS
                      ],
                      option.value
                    )
                      ? ''
                      : _.includes(
                            [TYPE.SELECT_MULTI, TYPE.SELECT_MULTI_USER, TYPE.SELECT_MULTI_GROUP],
                            item.property?.type
                          )
                        ? []
                        : null

                  updateStep({ ...item, condition: option.value, value: newValue }, index)
                }}
                key={index}
                searchable
              />
            </div>
          )}

          <div>
            {/* DATETIME Field */}
            {item.property?.type === TYPE.DATETIME && (
              <>
                {!_.includes(
                  [
                    PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE,
                    PROPERTY_SEARCH_ROW_CONDITION_VALUE.PREVIOUS_N_DAYS,
                    PROPERTY_SEARCH_ROW_CONDITION_VALUE.NEXT_N_DAYS
                  ],
                  item.condition
                ) && (
                  <>
                    <DatePicker
                      name={`${arrayHelpers.name}.${index}.value`}
                      selected={item.value as any}
                      onChange={(val: any) => {
                        item.value = val
                        updateStep(item, index)
                      }}
                      popoverTargetOffset="3px 0"
                      todayButton="Today"
                      dateFormatCalendar="MMMM"
                      dateFormat="MMM dd, yyyy"
                      showYearDropdown
                      className="form-control field-control w100p"
                      id="086010de-0cb7-408c-87b9-1bf37271dc60"
                    />
                  </>
                )}
                {_.includes(
                  [
                    PROPERTY_SEARCH_ROW_CONDITION_VALUE.PREVIOUS_N_DAYS,
                    PROPERTY_SEARCH_ROW_CONDITION_VALUE.NEXT_N_DAYS
                  ],
                  item.condition
                ) && (
                  <input
                    type="number"
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault()
                      }
                    }}
                    maxLength={5000}
                    minLength={0}
                    onChange={(ev) => {
                      if (ev.target.value && /^[0-9]*$/.test(ev.target.value)) {
                        item.value =
                          ev.target.value.length < 5
                            ? parseInt(ev.target.value)
                            : parseInt((ev.target.value + '').substring(0, 4))
                      } else {
                        item.value = ''
                      }
                      updateStep(item, index)
                    }}
                    name={`${arrayHelpers.name}.${index}.value`}
                    value={item.value as number}
                    className="field-control form-control w100p"
                    id="7414bb0a-dc2b-4ba8-bfc9-ab9151ad633f"
                  />
                )}
              </>
            )}

            {/* STRING Field */}
            {item.property?.type === TYPE.STRING && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <input
                    type="text"
                    onChange={(e) => {
                      item.value = e.target.value
                      updateStep(item, index)
                    }}
                    value={item.value as string}
                    className={'field-control form-control w100p'}
                    name={`${arrayHelpers.name}.${index}.value`}
                    maxLength={255}
                    id="88360c70-169f-42bb-af48-ac3620d3db43"
                  />
                )}
              </>
            )}

            {/* NUMBER Field */}
            {item.property?.type === TYPE.NUMBER && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <input
                    type="number"
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault()
                      }
                    }}
                    onChange={(ev) => {
                      if (ev.target.value && /^[0-9]*$/.test(ev.target.value)) {
                        item.value =
                          ev.target.value.length < 10
                            ? parseInt(ev.target.value)
                            : parseInt((ev.target.value + '').substring(0, 9))
                      } else {
                        item.value = ''
                      }
                      updateStep(item, index)
                    }}
                    name={`${arrayHelpers.name}.${index}.value`}
                    value={item.value as number}
                    className="field-control form-control w100p"
                    id="4cc5128d-4712-46e1-b40a-4d3356d3ccd2"
                  />
                )}
              </>
            )}

            {/* ARTIFACT Field */}
            {item.property?.type === TYPE.ARTIFACT && (
              <>
                {item.condition === PROPERTY_SEARCH_ROW_CONDITION_VALUE.EQUALS && (
                  <Select
                    multi={false}
                    options={
                      item.property.definition?.options?.[0]?.label
                        ? item.property.definition.options
                        : convertToArrayOfObjects(item.property.definition?.options || [])
                    }
                    onChange={(option: any) => {
                      item.value = option ? option.value : null
                      updateStep(item, index)
                    }}
                    value={item.value}
                    name={`${arrayHelpers.name}.${index}.value`}
                    showNewOptionAtTop={true}
                    className="field-control w100p"
                    id="7651d6a5-d1ca-4fd7-80c1-9d26be936efa"
                  />
                )}
                {item.condition === PROPERTY_SEARCH_ROW_CONDITION_VALUE.CONTAINS && (
                  <input
                    type="text"
                    onChange={(e) => {
                      item.value = e.target.value
                      updateStep(item, index)
                    }}
                    value={item.value as string}
                    className={'field-control form-control w100p'}
                    name={`${arrayHelpers.name}.${index}.value`}
                    maxLength={255}
                    id="88360c70-169f-42bb-af48-ac3620d3db43"
                  />
                )}
              </>
            )}

            {/* SELECT_ONE Field */}
            {item.property?.type === TYPE.SELECT_ONE && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <Select
                    multi={false}
                    options={
                      item.property.definition?.options?.[0]?.label
                        ? item.property.definition.options
                        : convertToArrayOfObjects(item.property.definition?.options || [])
                    }
                    onChange={(option: any) => {
                      item.value = option ? option.value : null
                      updateStep(item, index)
                    }}
                    value={item.value}
                    name={`${arrayHelpers.name}.${index}.value`}
                    showNewOptionAtTop={true}
                    className="field-control w100p"
                    id="e2d3831e-a89e-4221-a29c-4d37d1c58bfd"
                  />
                )}
              </>
            )}

            {/* SELECT_MULTI Field */}
            {item.property?.type === TYPE.SELECT_MULTI && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <Select
                    key={`${index}-${item.condition}`}
                    multi={true}
                    options={
                      item.property.definition?.options?.[0]?.label
                        ? item.property.definition.options
                        : convertToArrayOfObjects(item.property.definition?.options || [])
                    }
                    onChange={(option: any) => {
                      item.value = option ? option.map((o: any) => o.value) : []
                      updateStep(item, index)
                    }}
                    value={item.value}
                    name={`${arrayHelpers.name}.${index}.value`}
                    showNewOptionAtTop={true}
                    className="field-control w100p"
                    id="d8594f40-8ba9-43e2-814b-99f589446f0a"
                  />
                )}
              </>
            )}

            {/* SELECT_ONE_USER Field */}
            {item.property?.type === TYPE.SELECT_ONE_USER && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <SelectUserGroup
                    isMulti={false}
                    value={typeof item.value === 'string' ? item.value : null}
                    onlyUsers={true}
                    key={index}
                    name={`${arrayHelpers.name}.${index}.value`}
                    onChange={(option: any) => {
                      item.value = option ? option : null
                      updateStep(item, index)
                    }}
                  />
                )}
              </>
            )}

            {/* SELECT_MULTI_USER Field */}
            {item.property?.type === TYPE.SELECT_MULTI_USER && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <SelectUserGroup
                    isMulti={true}
                    onlyUsers={true}
                    value={Array.isArray(item.value) ? item.value : []}
                    key={`${index}-${item.condition}`}
                    name={`${arrayHelpers.name}.${index}.value`}
                    onChange={(option: any) => {
                      item.value = option ? option : []
                      updateStep(item, index)
                    }}
                  />
                )}
              </>
            )}

            {/* SELECT_ONE_GROUP Field */}
            {item.property?.type === TYPE.SELECT_ONE_GROUP && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <SelectUserGroup
                    isMulti={false}
                    value={typeof item.value === 'string' ? item.value : null}
                    onlyGroups={true}
                    key={index}
                    name={`${arrayHelpers.name}.${index}.value`}
                    onChange={(option: any) => {
                      item.value = option ? option : null
                      updateStep(item, index)
                    }}
                  />
                )}
              </>
            )}

            {/* SELECT_MULTI_GROUP Field */}
            {item.property?.type === TYPE.SELECT_MULTI_GROUP && (
              <>
                {item.condition !== PROPERTY_SEARCH_ROW_CONDITION_VALUE.USAGE && (
                  <SelectUserGroup
                    isMulti={true}
                    onlyGroups={true}
                    value={Array.isArray(item.value) ? item.value : []}
                    key={`${index}-${item.condition}`}
                    name={`${arrayHelpers.name}.${index}.value`}
                    onChange={(option: any) => {
                      item.value = option ? option : []
                      updateStep(item, index)
                    }}
                  />
                )}
              </>
            )}

            <CustomErrorMessage name={`${arrayHelpers.name}.${index}.value`} />

            <div className="d-flex gap-4 justify-content-start">
              <Form.Check
                name={`${arrayHelpers.name}.${index}.includeInherited`}
                checked={item.includeInherited}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  item.includeInherited = e.target.checked
                  updateStep(item, index)
                }}
                className="mb0"
                id="52ae7ffa-fbef-4c40-8c73-fe173470f1ef"
              />
              <Form.Label style={{ marginLeft: '6px' }}>
                {translate('REPORTS_FILTER_LABEL_INCLUDE_INHERITED')}
              </Form.Label>
            </div>
          </div>
        </div>
      ))}

      {!values || !values.length ? <CustomErrorMessage name={`${arrayHelpers.name}`} /> : null}

      <button
        data-action={'add-property'}
        className="btn btn-chita btn-add-step btn-second"
        type="button"
        onClick={addStep}
      >
        {translate('BROWSER_FILE_SETTINGS_APPROVERS_BTN_ADD_PROPERTY')}
      </button>
    </FormGroup>
  )
}

export default PropertyFields
